<template>
  <aside class="project-tree-menu">
    <div class="tree-header">
      <div class="search-box">
        <b-input
          :value="searchString"
          @input="updateSearchString"
          placeholder="Search folders..."
          type="search"
          icon="search"
          size="is-small"
        />
      </div>
      <div class="tree-filter">
        <div class="field has-addons">
          <p class="control">
            <button
              class="button is-small"
              :class="{ 'is-primary': all }"
              @click="setAllFilter(true)"
              title="Show all folders"
            >
              All
            </button>
          </p>
          <p class="control">
            <button
              class="button is-small"
              :class="{ 'is-primary': !all }"
              @click="setAllFilter(false)"
              title="Show my folders"
            >
              My
            </button>
          </p>
          <p class="control ml-2">
            <button class="button is-small" @click="toggleSortOrder" :title="sortTitle">
              <span class="icon is-small">
                <i :class="sortIconClass"></i>
              </span>
            </button>
          </p>
        </div>
      </div>
    </div>
    <ul class="menu-list">
      <li v-for="project in filteredProjects" :key="project.id" class="project-item">
        <a 
          @click="toggleProject(project)" 
          @contextmenu.prevent="showContextMenu($event, 'project', project)"
          :class="{'is-active': selectedItemType === 'project' && selectedItem && selectedItem.id === project.id}"
          class="project-link"
        >
          <span class="icon is-small expand-icon">
            <i :class="project.isExpanded ? 'fas fa-angle-down' : 'fas fa-angle-right'"></i>
          </span>
          <span class="icon is-small folder-icon">
            <i class="fas fa-folder"></i>
          </span>
          <span class="project-name">{{ project.name }}</span>
        </a>
        <ul v-if="project.isExpanded" class="image-group-list">
          <li v-for="imageGroup in getFilteredImageGroups(project.imageGroups)" :key="imageGroup.id" class="image-group-item">
            <a 
              @click="selectImageGroup(imageGroup)" 
              @contextmenu.prevent="showContextMenu($event, 'imageGroup', imageGroup)"
              :class="{'is-active': selectedItemType === 'imageGroup' && selectedItem && selectedItem.id === imageGroup.id}"
              class="image-group-link"
            >
              <span class="icon is-small folder-icon">
                <i class="fas fa-folder"></i>
              </span>
              <span class="image-group-name">{{ imageGroup.name }}</span>
            </a>
          </li>
        </ul>
      </li>
    </ul>
    <div v-if="filteredProjects.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-folder-open"></i>
      </div>
      <p class="empty-text">
        {{ searchString ? 'No folders match your search' : 'No projects found' }}
      </p>
    </div>
    <ContextMenu ref="contextMenu" :items="contextMenuItems" @item-click="handleMenuItemClick" v-click-outside="closeContextMenu" />
  </aside>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ContextMenu from './ContextMenu.vue';

export default {
  name: 'ProjectTree',
  components: {
    ContextMenu
  },
  props: {
    selectedItem: {
      type: Object,
      default: null
    },
    selectedItemType: {
      type: String,
      default: null
    }
  },
  computed: {
    ...mapState('project-tree', ['projects', 'all', 'searchString', 'sortOrder']),
    ...mapGetters('project-tree', ['filteredProjects', 'getFilteredImageGroups']),
    
    sortIconClass() {
      if (this.sortOrder === 'asc') return 'fas fa-sort-alpha-down';
      if (this.sortOrder === 'desc') return 'fas fa-sort-alpha-up';
      return 'fas fa-sort';
    },
    
    sortTitle() {
      if (this.sortOrder === 'asc') return 'Sort: Z-A';
      if (this.sortOrder === 'desc') return 'Sort: Default';
      return 'Sort: A-Z';
    }
  },
  methods: {
    ...mapActions('project-tree', ['toggleProject', 'selectImageGroup', 'setAllFilter', 'setSortOrder']),
    
    toggleSortOrder() {
      let newOrder;
      if (this.sortOrder === null) {
        newOrder = 'asc';
      } else if (this.sortOrder === 'asc') {
        newOrder = 'desc';
      } else {
        newOrder = null;
      }
      this.setSortOrder(newOrder);
    },
    
    updateSearchString(value) {
      this.$store.commit('project-tree/SET_SEARCH_STRING', value);
    },
    
    showContextMenu(event, type, item) {
      this.contextMenuItem = item;
      this.contextMenuType = type;
      if (type === 'project') {
        this.contextMenuItems = [
          {
            label: 'Add Sub-folder',
            action: 'add-subfolder',
            icon: 'fas fa-folder-plus'
          },
          {
            label: 'Rename',
            action: 'rename',
            icon: 'fas fa-edit'
          }
        ];
      } else if (type === 'imageGroup') {
        this.contextMenuItems = [
          {
            label: 'Rename',
            action: 'rename',
            icon: 'fas fa-edit'
          },
          {
            label: 'Delete',
            action: 'delete',
            icon: 'fas fa-trash'
          }
        ];
      }
      this.$refs.contextMenu.open(event);
    },
    closeContextMenu() {
      if (this.$refs.contextMenu) {
        this.$refs.contextMenu.close();
      }
    },
    handleMenuItemClick(item) {
      this.closeContextMenu();
      if (item.action === 'add-subfolder') {
        this.$emit('add-subfolder', this.contextMenuItem);
      }
      else if (item.action === 'rename') {
        this.$emit('rename-item', { type: this.contextMenuType, item: this.contextMenuItem });
      }
      else if (item.action === 'delete') {
        this.$emit('delete-item', { type: this.contextMenuType, item: this.contextMenuItem });
      }
    }
  },
  
  data() {
    return {
      contextMenuItem: null,
      contextMenuType: null,
      contextMenuItems: []
    };
  }
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/dark-variables.scss';

.project-tree-menu {
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .tree-header {
    flex-shrink: 0;
    padding: 0 0 1rem 0;
    border-bottom: 1px solid $dark-border-color;
    margin-bottom: 1rem;

    .search-box {
      margin-bottom: 0.75rem;

      ::v-deep .control {
        width: 100%;
      }

      ::v-deep .input {
        background-color: $dark-bg-hover;
        border-color: $dark-border-color;
        color: $dark-text-primary;

        &:focus {
          border-color: $primary;
          box-shadow: 0 0 0 0.125em rgba($primary, 0.25);
        }

        &::placeholder {
          color: $dark-text-disabled;
        }
      }

      ::v-deep .icon {
        color: $dark-text-disabled;
      }
    }

    .tree-filter {
      display: flex;
      justify-content: flex-end;
      
      .button {
        border-radius: 4px;
        padding: 0.25rem 0.75rem;
        font-size: 0.85em;
        margin-left: 0.25rem;
        
        &.is-primary {
          background-color: $primary;
          border-color: $primary;
          color: $dark-bg-primary;
        }
      }
    }
  }
  
  .menu-list {
    margin: 0;
    padding: 0;
    flex-grow: 1;
    overflow-x: hidden;
    overflow-y: auto;
    
    .project-item {
      margin-bottom: 0.5rem;
      
      .project-link {
        display: flex;
        align-items: center;
        padding: 0.75rem 1rem;
        border-radius: 6px;
        transition: all 0.2s ease;
        color: $dark-text-primary;
        text-decoration: none;
        font-weight: 500;
        position: relative;
        
        &:hover {
          background-color: $dark-bg-hover;
          transform: translateX(4px);
        }
        
        &.is-active {
          background-color: rgba($primary, 0.2);
          border-left: 3px solid $primary;
          color: $primary;
          
          .folder-icon i {
            color: $primary;
          }
        }
        
        .expand-icon {
          margin-right: 8px;
          opacity: 0.7;
          transition: transform 0.2s ease;
        }
        
        .folder-icon {
          margin-right: 10px;
          color: $warning;
        }
        
        .project-name {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      
      .image-group-list {
        margin: 0.5rem 0 0 1.5rem;
        padding: 0;
        list-style: none;
        
        .image-group-item {
          margin-bottom: 0.25rem;
          
          .image-group-link {
            display: flex;
            align-items: center;
            padding: 0.5rem 0.75rem;
            border-radius: 4px;
            transition: all 0.2s ease;
            color: $dark-text-secondary;
            text-decoration: none;
            font-size: 0.95em;
            
            &:hover {
              background-color: $dark-bg-hover;
              color: $dark-text-primary;
            }
            
            &.is-active {
              background-color: rgba($info, 0.2);
              color: $info;
              
              .folder-icon i {
                color: $info;
              }
            }
            
            .folder-icon {
              margin-right: 8px;
              color: $warning;
            }
            
            .image-group-name {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }
      }
    }
  }
  
  .empty-state {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
    color: $dark-text-disabled;
    
    .empty-icon {
      font-size: 2.5rem;
      margin-bottom: 1rem;
      opacity: 0.6;
    }
    
    .empty-text {
      font-size: 0.9rem;
      opacity: 0.8;
    }
  }
}
</style>